import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CACHE_EXPIRATION_TIME, DESKTOP_SCREEN_SIZE } from 'app/common/personalConstants';
import { DocumentsState } from 'app/shared/models/claim-tracker.model';
import { ClaimsFnolService } from 'app/shared/services/claims-fnol.service';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';
import { environment } from 'environments/environment';
import { catchError, forkJoin, map, of, shareReplay, Subscription } from 'rxjs';
import { DocumentUploadComponent } from './document-upload/document-upload.component';

interface DocumentItem {
    fileName: string;
    uploadedBy: string;
    dateAdded: string;
    docType: 'Estimates' | 'Photos' | 'Other';
    fileId?: string;
    estimateNumber?: number;
    originalFileName?: string;
}

@Component({
    selector: 'is-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.scss'],
    standalone: false
})
export class DocumentsComponent implements OnInit, OnDestroy {
    static readonly LOG_PREFIX = 'Documents';
    private static readonly DESKTOP_LIMIT = 10;
    private static readonly MOBILE_LIMIT = 6;

    @ViewChild(DocumentUploadComponent) documentUploadComponent: DocumentUploadComponent;
    @Input() claimNumber!: string;
    @Input() useCaching!: boolean;
    @Input() displayUnregistered: boolean = false;
    environment = environment;

    get isUnregisteredUser(): boolean {
        return this.displayUnregistered;
    }

    documentsList: DocumentItem[] = [];
    paginatedDocuments: DocumentItem[] = [];
    private cachedFilteredDocuments: DocumentItem[] = [];
    docState: DocumentsState = DocumentsState.LOADING;
    readonly DocumentsStateEnum = DocumentsState;
    // selectedFilter: 'All' | 'Estimates' | 'Photos' | 'Other' = 'All';
    // documentFilters: Array<'All' | 'Estimates' | 'Photos' | 'Other'> = ['All', 'Estimates', 'Photos', 'Other'];
    selectedFilter: 'All' | 'Photos' | 'Other' = 'All';
    documentFilters: Array<'All' | 'Photos' | 'Other'> = ['All', 'Photos', 'Other'];
    accessTokenResponse: string = '';
    sortOrders: { fileName: 'asc' | 'desc' | 'unsorted'; uploadedBy: 'asc' | 'desc' | 'unsorted'; dateAdded: 'asc' | 'desc' | 'unsorted' } = {
        fileName: 'unsorted',
        uploadedBy: 'unsorted',
        dateAdded: 'desc'
    };
    currentPage = 1;
    totalPages = 1;
    limit = DocumentsComponent.DESKTOP_LIMIT;
    private breakpointSubscription: Subscription;
    private documentSubscription: Subscription;

    policyNumber!: string;
    userType!: string;

    constructor (
        private bp: BreakpointObserver,
        private logger: LoggerService,
        private readonly sessionService: SessionService,
        private claimsFnolService: ClaimsFnolService,
        private claimsTrackerService: ClaimsTrackerService,
        private utils: UtilService,
        private router: Router
    ) { }

    ngOnInit(): void {
        if (this.displayUnregistered) {
            this.docState = DocumentsState.NO_DOCUMENTS;
            return;
        }
        this.initializeSessionData();
        this.initializeLogger();
        this.logger.info('DocumentsComponent initialized for claim:', this.claimNumber);
        const Documents_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('DOCUMENTS_DATA') : undefined;
        if (
            typeof Documents_CACHE_DATA === 'undefined' ||
            Documents_CACHE_DATA.id !== this.claimNumber
        ) {
            this.loadDocuments();
        } else {
            const response = Documents_CACHE_DATA.value;
            this.loadDocumentsData(response);
        }

        this.breakpointSubscription = this.bp.observe('(max-width: 575px)')
            .pipe(
                map((result) => result.matches),
                shareReplay(1)
            )
            .subscribe((isMobile) => {
                this.limit = isMobile ? DocumentsComponent.MOBILE_LIMIT : DocumentsComponent.DESKTOP_LIMIT;
                this.currentPage = 1;
                this.updatePagination();
            });
    }

    private initializeLogger(): void {
        this.logger.setComponentName('DocumentsComponent');
        this.logger.setPageName('Documents');
        this.logger.info(`${DocumentsComponent.LOG_PREFIX} - Component Initialized`, {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber,
            userType: this.userType
            });
    }
    private initializeSessionData(): void {
        if (!this.claimNumber) {
            this.claimNumber = this.sessionService.getClaimNumber();
        }
        this.policyNumber = this.sessionService.getPolicyNumber();
        this.userType = this.sessionService.getUserType();
    }

    ngOnDestroy(): void {
        if (this.breakpointSubscription) {
            this.breakpointSubscription.unsubscribe();
        }
        if (this.documentSubscription) {
            this.documentSubscription.unsubscribe();
        }
    }

    loadDocuments(): void {
        if (this.documentSubscription) {
            this.documentSubscription.unsubscribe();
        }

        this.documentsList = [];
        this.docState = DocumentsState.LOADING;

        this.documentSubscription = forkJoin({
            contentFilesResponse: this.claimsFnolService.buildGetContentFiles(this.claimNumber).pipe(
                catchError((error) => {
                    this.logger.error('Property Documents: Error loading content files:', error);
                    return of(null);
                })
            ),
            // propertyEstimatesResponse: this.claimsTrackerService.getClaimPropertyEstimates(this.claimNumber).pipe(
            //     catchError((error) => {
            //         this.logger.error('Property Documents: Error loading property estimates:', error);
            //         return of(null);
            //     })
            // ),
            accessTokenResponse: this.claimsTrackerService.getContentAccessToken(this.claimNumber).pipe(
                catchError((error) => {
                    this.logger.error('Property Documents: Error loading access token:', error);
                    return of(null);
                })
            )
        }).subscribe({
            next: (response) => {
                if (response.accessTokenResponse && response.accessTokenResponse.token) {
                    this.accessTokenResponse = response.accessTokenResponse.token;
                }

                const contentFilesCode = response.contentFilesResponse?.code;
                // const propertyEstimatesCode = response.propertyEstimatesResponse?.code;

                const isContentFilesError = !response.contentFilesResponse || this.isHttpError(contentFilesCode);
                // const isPropertyEstimatesError = !response.propertyEstimatesResponse || this.isHttpError(propertyEstimatesCode);

                if (isContentFilesError /* || isPropertyEstimatesError */) {
                    this.docState = DocumentsState.ERROR;
                    return;
                }

                this.utils.setWithExpiry(
                    'DOCUMENTS_DATA',
                    response,
                    CACHE_EXPIRATION_TIME,
                    this.claimNumber
                );
                this.loadDocumentsData(response);
            },
            error: (error) => {
                this.logger.error('Error loading documents:', error);
                this.docState = DocumentsState.ERROR;
            }
        });
    }

    loadDocumentsData(response: any): void {
        if (response.accessTokenResponse && response.accessTokenResponse.token) {
            this.accessTokenResponse = response.accessTokenResponse.token;
        }

        if (response.contentFilesResponse && response.contentFilesResponse.contentFiles) {
            response.contentFilesResponse.contentFiles.forEach((element: any) => {
                this.documentsList.push({
                    fileName: element.fileName || 'Unknown',
                    uploadedBy: this.formatName(element.created_by),
                    dateAdded: element.created_time || 'Unknown',
                    docType: this.determineDocType(element),
                    fileId: element.fileId || element.publicId
                });
            });
        }

        // if (response.propertyEstimatesResponse && Array.isArray(response.propertyEstimatesResponse.propertyEstimates)) {
        //     response.propertyEstimatesResponse.propertyEstimates
        //         .sort((a, b) => new Date(a.estimateDocument?.dateCreated).getTime() - new Date(b.estimateDocument?.dateCreated).getTime())
        //         .forEach((estimate: any, index: number) => {
        //             this.documentsList.push({
        //                 fileName: 'Personal Property Estimate',
        //                 uploadedBy: this.formatName(estimate.source?.estimator),
        //                 dateAdded: estimate?.assessmentDate || 'Unknown',
        //                 docType: 'Estimates',
        //                 fileId: estimate.estimateDocument?.fileId || estimate.estimateDocument?.publicId,
        //                 estimateNumber: estimate.versionNumber,
        //                 originalFileName: estimate.estimateDocument?.name || 'Personal Property Estimate'
        //             });
        //         });
        // }

        if (this.documentsList.length === 0) {
            this.docState = DocumentsState.NO_DOCUMENTS;
        } else {
            this.docState = DocumentsState.HAS_DOCUMENTS;
            this.currentPage = 1;
            this.cachedFilteredDocuments = this.getFilteredDocuments();
            this.sortDocuments('dateAdded', 'desc');
        }
    }

    private determineDocType(file: any): 'Estimates' | 'Photos' | 'Other' {
        const fileName = (file.fileName || '').toLowerCase();
        if (fileName.match(/\.(jpg|jpeg|png|gif|bmp|tif|tiff|webp)$/i)) {
            return 'Photos';
        }
        return 'Other';
    }

    refreshDocuments(): void {
        this.logger.info('Refreshing documents for claim:', this.claimNumber);
        this.documentsList = [];
        this.cachedFilteredDocuments = [];
        this.paginatedDocuments = [];
        this.currentPage = 1;
        this.selectedFilter = 'All';
        this.loadDocuments();
    }

    filterDocuments(filterType: 'All' | 'Photos' | 'Other'): void {
        if (this.selectedFilter === filterType && filterType !== 'All') {
            this.selectedFilter = 'All';
            this.logger.info('Filter deselected, showing all documents');
        } else {
            this.selectedFilter = filterType;
            this.logger.info(`Filter documents by type ${filterType}`);
        }
        this.currentPage = 1;
        this.cachedFilteredDocuments = this.getFilteredDocuments();
        this.sortDocuments('dateAdded', 'desc');
    }

    sortDocuments(
        sortBy: 'fileName' | 'uploadedBy' | 'dateAdded',
        order: 'asc' | 'desc'
    ): void {
        this.resetOtherSortOrders(sortBy);
        this.cachedFilteredDocuments = [...this.cachedFilteredDocuments].sort((a, b) => {
            let compareA: string;
            let compareB: string;

            switch (sortBy) {
                case 'fileName':
                    compareA = a.fileName.toLowerCase();
                    compareB = b.fileName.toLowerCase();
                    break;
                case 'uploadedBy':
                    compareA = a.uploadedBy.toLowerCase();
                    compareB = b.uploadedBy.toLowerCase();
                    break;
                case 'dateAdded':
                    const dateA = new Date(a.dateAdded);
                    const dateB = new Date(b.dateAdded);
                    compareA = dateA.getTime().toString();
                    compareB = dateB.getTime().toString();
                    break;
                default:
                    return 0;
            }

            if (compareA < compareB) {
                return order === 'asc' ? -1 : 1;
            }
            if (compareA > compareB) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
        this.sortOrders[sortBy] = order;
        this.currentPage = 1;
        this.updatePagination();
    }

    onSortIconClick(column: 'fileName' | 'uploadedBy' | 'dateAdded'): void {
        const currentOrder = this.sortOrders[column];
        let nextOrder: 'asc' | 'desc' | 'unsorted';

        if (currentOrder === 'unsorted') {
            nextOrder = 'asc';
        } else {
            nextOrder = currentOrder === 'asc' ? 'desc' : 'asc';
        }

        this.sortDocuments(column, nextOrder);
    }

    private resetOtherSortOrders(activeColumn: 'fileName' | 'uploadedBy' | 'dateAdded'): void {
        Object.keys(this.sortOrders).forEach((key) => {
            if (key !== activeColumn) {
                this.sortOrders[key as 'fileName' | 'uploadedBy' | 'dateAdded'] = 'unsorted';
            }
        });
    }

    private updatePagination(): void {
        this.totalPages = Math.ceil(this.cachedFilteredDocuments.length / this.limit);
        this.paginatedDocuments = this.getPaginatedDocuments(this.cachedFilteredDocuments);
    }

    private getFilteredDocuments(): DocumentItem[] {
        if (this.selectedFilter === 'All') {
            return this.documentsList;
        }
        return this.documentsList.filter((doc) => doc.docType === this.selectedFilter);
    }

    private getPaginatedDocuments(documents: DocumentItem[]): DocumentItem[] {
        const startIndex = (this.currentPage - 1) * this.limit;
        const endIndex = startIndex + this.limit;
        return documents.slice(startIndex, endIndex);
    }

    isHttpError(code: number | undefined): boolean {
        return typeof code === 'number' && code >= 400 && code < 600;
    }

    gotoPage(event: Event): void {
        const customEvent = <CustomEvent<{ page: number }>>event;
        this.currentPage = customEvent.detail.page;
        this.updatePagination();
    }

    private formatName(name: string): string {
        if (!name) {
            return 'Unknown';
        }
        return name.toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    openDocument(document: DocumentItem): void {
        if (!document.fileId) {
            this.logger.error('Property Tracker Documents: No file ID available for document:', document.fileName);
            return;
        }

        if (!this.accessTokenResponse) {
            this.logger.error('Property Tracker Documents: No access token available for preview');
            return;
        }

        const isEstimate = document.docType === 'Estimates';
        const logMessage = isEstimate ? `Estimate opened - Estimate Number: ${document.estimateNumber}` : 'Other Document opened';

        this.logger.info(`Property Tracker Documents: ${logMessage}`, {
            fileName: document.fileName,
            fileId: document.fileId,
            docType: document.docType,
            claimNumber: this.claimNumber,
            dateAdded: document.dateAdded
        });

        const fileNameForPreview = document.originalFileName || document.fileName;
        this.openDocumentInNewWindow(document.fileId, fileNameForPreview);
    }

    private openDocumentInNewWindow(fileId: string, fileName: string): void {
        if (!this.accessTokenResponse) {
            this.logger.error('Access token is not available for preview');
            return;
        }

        const width = 1200;
        const height = 800;
        const left = (screen.width - width) / 2;
        const top = (screen.height - height) / 2;

        const newWindow = window.open(
            '',
            '_blank',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!newWindow) {
            this.logger.error('Failed to open new window - pop-up blocked');
            return;
        }

        const token = this.accessTokenResponse;
        const fileIdValue = fileId;
        const escapeFileName = this.escapeHtml(fileName);

        newWindow.document.write(`
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1">
                <title>${escapeFileName}</title>
                <link rel="stylesheet" href="https://cdn01.boxcdn.net/platform/preview/2.80.0/en-US/preview.css" />
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    html, body {
                        width: 100%;
                        height: 100%;
                    }
                    .preview-container {
                        width: 100%;
                        height: 100%;
                    }
                </style>
            </head>
            <body>
                <div id="preview" class="preview-container"></div>
                <script src="https://cdn01.boxcdn.net/platform/preview/2.80.0/en-US/preview.js"><\/script>
                <script>
                    (function() {
                        var token = '${token}';
                        var fileId = '${fileIdValue}';

                        function initPreview() {
                            try {
                                if (typeof Box !== 'undefined' && typeof Box.Preview !== 'undefined') {
                                    var preview = new Box.Preview();
                                    preview.show(fileId,
                                    token,
                                    {
                                        container: '.preview-container',
                                        hasHeader: true,
                                        showDownload: true,
                                        showPrint: true
                                    });
                                } else {
                                    throw new Error('Preview not available');
                                }
                            } catch (error) {
                                console.error('Preview error:', error);
                                document.getElementById('preview').innerHTML = '<div style="padding:40px;text-align:center;color:#d32f2f;"><h2>Error Loading Document</h2><p>' + error.message + '</p></div>';
                            }
                        }

                        if (document.readyState === 'loading') {
                            document.addEventListener('DOMContentLoaded', initPreview);
                        } else {
                            initPreview();
                        }
                    })();
                </script>
            </body>
            </html>
        `);
        newWindow.document.close();
        this.logger.info('Document preview window opened for:', fileName);
    }

    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    @HostListener('window:resize')
    onResize(): void {
        if (window.innerWidth >= DESKTOP_SCREEN_SIZE) {
            this.sessionService.setSessionItem('tab', 'Documents');
            this.router.navigate(['/property/tracker']);
        }
    }
}
